import { useState } from 'react';
import {
  AFFILIATE_EARNINGS_PAYMENT_STATES,
  AFFILIATE_EARNINGS_RANGE_IDS,
  AFFILIATE_EARNINGS_SUPPORT,
  affiliateEarningsRangeIssue,
  withAffiliateEarningsRange,
} from '@wlb/shared/api/affiliate/affiliateEarnings';
import type {
  AffiliateEarningsFilter,
  AffiliateEarningsPaymentState,
  AffiliateEarningsRangeId,
  AffiliateEarningsSummary,
} from '@wlb/shared/api/affiliate/affiliateTypes';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { FormError } from '../../components/ui/FormError';
import { Icon } from '../../components/ui/Icon';
import { SelectField } from '../../components/ui/SelectField';
import { TextField } from '../../components/ui/TextField';
import { formatCount, formatCurrency } from '../../utils/format';
import '../../components/ui/ui.css';
import '../../pages/pages.css';

interface AffiliateEarningsFiltersProps {
  filter: AffiliateEarningsFilter;
  onFilterChange: (next: AffiliateEarningsFilter) => void;
  summary: AffiliateEarningsSummary | null;
}

const PAYMENT_ENABLED = AFFILIATE_EARNINGS_SUPPORT.paymentState;

const PAYMENT_NOTE_ID = 'affiliate-earnings-payment-note';
/** One message for a range spanning two inputs, so both point at the same id. */
const RANGE_ERROR_ID = 'affiliate-earnings-range-error';
const EARNINGS_CONTENT_ID = 'affiliate-earnings-content';

/**
 * Earnings period / payment-status filters and the roll-up tiles for the
 * affiliates list.
 *
 * The period is sent to `GetAllAffiliate`, which takes a date interval. The
 * payment-status control stays disabled: no affiliate read filters rows by
 * paid/due, and that schema is `additionalProperties: false`, so sending one
 * would be a 400 rather than a narrower list. The paid/owed figures live per
 * affiliate — `AffiliateUserResponse` carries none — so the payout dialog
 * reports them instead of these tiles.
 */
export function AffiliateEarningsFilters({
  filter,
  onFilterChange,
  summary,
}: AffiliateEarningsFiltersProps) {
  const { t } = useTranslation('admin');
  const [expanded, setExpanded] = useState(false);

  const rangeIssue = affiliateEarningsRangeIssue(filter);
  const rangeError =
    rangeIssue === 'none'
      ? null
      : t(
          rangeIssue === 'inverted'
            ? 'affiliates.earnings.rangeInvalid'
            : 'affiliates.earnings.rangeIncomplete',
        );

  const tiles: readonly { key: string; label: string; value: string }[] = [
    {
      key: 'totalEarnings',
      label: t('affiliates.earnings.tiles.totalEarnings'),
      value: formatCurrency(summary?.totalEarnings ?? 0),
    },
    {
      key: 'downloads',
      label: t('affiliates.earnings.tiles.downloads'),
      value: formatCount(summary?.usageCount ?? 0),
    },
  ];

  return (
    <section className="page__card affiliate-earnings">
      <div className="affiliate-earnings__header">
        <h2 className="page__section-title">{t('affiliates.earnings.legend')}</h2>
        <button
          type="button"
          className="affiliate-earnings__toggle"
          aria-expanded={expanded}
          aria-controls={EARNINGS_CONTENT_ID}
          onClick={() => setExpanded(current => !current)}
        >
          <span>
            {t(
              expanded
                ? 'affiliates.earnings.hide'
                : 'affiliates.earnings.show',
            )}
          </span>
          <Icon name="chevronDown" size="sm" />
        </button>
      </div>

      <div
        className="affiliate-earnings__content"
        id={EARNINGS_CONTENT_ID}
        hidden={!expanded}
      >
          <div className="page__toolbar affiliate-earnings__filters">
            <div className="page__toolbar-field">
              <SelectField
                label={t('affiliates.earnings.rangeLabel')}
                value={filter.rangeId}
                options={AFFILIATE_EARNINGS_RANGE_IDS.map(rangeId => ({
                  value: rangeId,
                  label: t(`affiliates.earnings.ranges.${rangeId}`),
                }))}
                onChange={event =>
                  onFilterChange(
                    withAffiliateEarningsRange(
                      filter,
                      event.target.value as AffiliateEarningsRangeId,
                    ),
                  )
                }
              />
            </div>

            {filter.rangeId === 'custom' ? (
              <>
                <div className="page__toolbar-field">
                  {/* The error belongs to the pair, so it is rendered once below and
                      both inputs describe themselves with it rather than each
                      `TextField` announcing the same sentence. */}
                  <TextField
                    label={t('affiliates.earnings.startDate')}
                    type="date"
                    value={filter.startDate}
                    max={filter.endDate || undefined}
                    aria-invalid={rangeError ? true : undefined}
                    aria-describedby={rangeError ? RANGE_ERROR_ID : undefined}
                    onChange={event =>
                      onFilterChange({ ...filter, startDate: event.target.value })
                    }
                  />
                </div>
                <div className="page__toolbar-field">
                  <TextField
                    label={t('affiliates.earnings.endDate')}
                    type="date"
                    value={filter.endDate}
                    min={filter.startDate || undefined}
                    aria-invalid={rangeError ? true : undefined}
                    aria-describedby={rangeError ? RANGE_ERROR_ID : undefined}
                    onChange={event =>
                      onFilterChange({ ...filter, endDate: event.target.value })
                    }
                  />
                </div>
              </>
            ) : null}

            <div className="page__toolbar-field">
              <SelectField
                label={t('affiliates.earnings.paymentStateLabel')}
                value={filter.paymentState}
                disabled={!PAYMENT_ENABLED}
                aria-describedby={PAYMENT_ENABLED ? undefined : PAYMENT_NOTE_ID}
                options={AFFILIATE_EARNINGS_PAYMENT_STATES.map(state => ({
                  value: state,
                  label: t(`affiliates.earnings.paymentStates.${state}`),
                }))}
                onChange={event =>
                  onFilterChange({
                    ...filter,
                    paymentState: event.target.value as AffiliateEarningsPaymentState,
                  })
                }
              />
            </div>
          </div>

          <FormError variant="banner" message={rangeError} id={RANGE_ERROR_ID} />

          {PAYMENT_ENABLED ? null : (
            <p className="page__note" id={PAYMENT_NOTE_ID}>
              {t('affiliates.earnings.unsupportedPayment')}
            </p>
          )}

          <dl className="affiliate-earnings__tiles">
            {tiles.map(tile => (
              <div className="affiliate-earnings__tile" key={tile.key}>
                <dt className="affiliate-earnings__tile-label">{tile.label}</dt>
                <dd className="affiliate-earnings__tile-value">{tile.value}</dd>
              </div>
            ))}
          </dl>

          <p className="page__note">{t('affiliates.earnings.pageTotalNote')}</p>
      </div>
    </section>
  );
}
